import React, { useMemo, useState } from 'react';
import ModalLayer from './ModalLayer';
import { HOMEOWNER_UPLOAD_MAX_BYTES } from '../utils/fileUpload';
import {
  INVENTORY_TYPES,
  DETAIL_FIELDS,
  emptyForm,
  formFromSystem,
  extraFields,
  readDetails,
} from '../shared/inventoryItem';

const BRAND = '#59c4c4';

const InventoryItemForm = ({
  colors,
  existingData,
  initialType,
  nowYear,
  saving,
  resetAfterSave,
  items,
  onRemove,
  onCancel,
  onSave,
  onDone,
  onSkip,
}) => {
  const year = nowYear || new Date().getFullYear();
  const starting = useMemo(() => (
    initialType
      ? formFromSystem(initialType, existingData, year)
      : emptyForm()
  ), [initialType, existingData, year]);

  const [systemType, setSystemType] = useState(starting.systemType);
  const [brand, setBrand] = useState(starting.brand);
  const [whenMode, setWhenMode] = useState('year');
  const [installYear, setInstallYear] = useState(starting.installYear);
  const [age, setAge] = useState(starting.age);
  const [details, setDetails] = useState(starting.details);
  const [extras, setExtras] = useState(starting.extras);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [photoError, setPhotoError] = useState('');
  const [notice, setNotice] = useState('');

  const editing = Boolean(initialType);
  const legacyExtras = extraFields(existingData);
  const typeChoices = INVENTORY_TYPES;
  const fullPage = Boolean(onDone);
  const chipStyle = (selected) => ({
    minHeight: '44px',
    maxWidth: '100%',
    boxSizing: 'border-box',
    padding: '0.45rem 0.9rem',
    borderRadius: '999px',
    border: `1px solid ${selected ? BRAND : colors.border}`,
    background: selected ? BRAND : colors.surface,
    color: selected ? '#083838' : colors.text,
    fontWeight: 650,
    fontSize: '16px',
    whiteSpace: 'normal',
  });

  const inputStyle = {
    width: '100%',
    boxSizing: 'border-box',
    minHeight: '48px',
    padding: '0.75rem 0.9rem',
    borderRadius: '0.75rem',
    border: `1px solid ${colors.border}`,
    background: colors.surface,
    color: colors.text,
    fontSize: '16px',
  };

  const pickPhoto = (file) => {
    setPhotoError('');
    if (!file) {
      setPhotoFile(null);
      setPhotoPreview('');
      return;
    }
    if (file.size > HOMEOWNER_UPLOAD_MAX_BYTES) {
      setPhotoError('Use a photo of 50 MB or less.');
      return;
    }
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setPhotoPreview(typeof reader.result === 'string' ? reader.result : '');
    reader.readAsDataURL(file);
  };

  const draft = () => ({
    systemType,
    brand,
    installYear: whenMode === 'year' ? installYear : '',
    age: whenMode === 'age' ? age : '',
    details,
    extras,
    photoFile,
  });

  const resetForm = () => {
    setSystemType('');
    setBrand('');
    setInstallYear('');
    setAge('');
    setDetails(readDetails({}));
    setExtras({});
    setPhotoFile(null);
    setPhotoPreview('');
    setPhotoError('');
  };

  const addItem = async () => {
    if (!systemType || saving) return;
    const label = (typeChoices.find((type) => type.id === systemType) || {}).label || 'Item';
    try {
      await onSave(draft());
    } catch (error) {
      return;
    }
    setNotice(`Added ${label}.`);
    if (resetAfterSave && !initialType) resetForm();
  };

  const removeItem = async (item) => {
    if (!onRemove || saving) return;
    try {
      await onRemove(item.id);
    } catch (error) {
      return;
    }
    setNotice(`Removed ${item.label}.`);
  };

  const finish = () => {
    if (saving || !onDone) return;
    onDone(systemType ? draft() : null);
  };

  const submit = async (event) => {
    event.preventDefault();
    if (saving) return;
    if (onDone) {
      if (systemType) await addItem();
      return;
    }
    if (!systemType) return;
    try {
      await onSave(draft());
    } catch (error) {
      return;
    }
    if (resetAfterSave && !initialType) resetForm();
  };

  const primaryReady = Boolean(onDone) || (Boolean(systemType) && !saving);
  const dialog = (
    <div
      role="dialog"
      aria-label={editing ? 'Edit item' : 'Add item'}
      data-layout={fullPage ? 'page' : 'sheet'}
      className={fullPage ? 'add-item-page' : 'app-modal-card add-item-sheet'}
      style={{
        background: colors.background,
        color: colors.text,
        overflowX: 'hidden',
        boxSizing: 'border-box',
        width: '100%',
        maxWidth: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        ...(fullPage ? {
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          zIndex: 30,
          height: 'auto',
          maxHeight: 'none',
          overflow: 'hidden',
          paddingLeft: 'env(safe-area-inset-left, 0px)',
          paddingRight: 'env(safe-area-inset-right, 0px)',
        } : {
          height: '100%',
          overflow: 'hidden',
        }),
      }}
    >
      <form
        onSubmit={submit}
        style={{
          width: '100%',
          maxWidth: '560px',
          minWidth: 0,
          minHeight: 0,
          height: 'auto',
          margin: '0 auto',
          padding: 0,
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          flex: '1 1 auto',
        }}
      >
        <header
          className="add-item-header"
          style={{
            flex: '0 0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '0.75rem',
            boxSizing: 'border-box',
            paddingTop: fullPage ? 'calc(env(safe-area-inset-top, 0px) + 1.25rem)' : '0.35rem',
            paddingRight: '1.25rem',
            paddingBottom: '0.35rem',
            paddingLeft: '1.25rem',
          }}
        >
          <h2 style={{ margin: 0, fontSize: '1.75rem', fontWeight: 700, letterSpacing: '-0.02em' }}>{editing ? 'Edit item' : 'Add an item'}</h2>
          <button
            type="button"
            onClick={onCancel}
            style={{
              minHeight: '44px',
              padding: '0.4rem 0.2rem',
              border: 'none',
              background: 'transparent',
              color: BRAND,
              fontSize: '1.05rem',
              fontWeight: 600,
            }}
          >
            Cancel
          </button>
        </header>
        <div
          className="add-item-scroll"
          style={{
            flex: '1 1 auto',
            minHeight: 0,
            overflowX: 'hidden',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            touchAction: 'pan-y',
            padding: '0.5rem 1.25rem 1.5rem',
            boxSizing: 'border-box',
          }}
        >
        <p style={{ margin: '0 0 1rem', color: colors.textSecondary, fontSize: '1.05rem', lineHeight: 1.4 }}>
          {editing ? 'Update this item. Other items stay as they are.' : 'Pick a type. You can add the details later.'}
        </p>
        {notice ? (
          <p role="status" style={{ margin: '0 0 1rem', color: colors.text, fontWeight: 650 }}>{notice}</p>
        ) : null}
        {Array.isArray(items) && items.length > 0 ? (
          <ul aria-label="Added items" style={{ listStyle: 'none', margin: '0 0 1.25rem', padding: 0, display: 'grid', gap: '0.5rem' }}>
            {items.map((item) => (
              <li
                key={item.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '0.75rem',
                  minHeight: '52px',
                  padding: '0.65rem 0.85rem',
                  borderRadius: '0.75rem',
                  border: `1px solid ${colors.border}`,
                  background: colors.surface,
                }}
              >
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: 'block', fontWeight: 650 }}>{item.label}</span>
                  {item.detail ? (
                    <span style={{ display: 'block', color: colors.textSecondary, fontSize: '0.95rem' }}>{item.detail}</span>
                  ) : null}
                </span>
                <button
                  type="button"
                  onClick={() => removeItem(item)}
                  disabled={saving}
                  aria-label={`Remove ${item.label}`}
                  style={{
                    flex: '0 0 auto',
                    minHeight: '44px',
                    padding: '0.25rem 0.2rem',
                    border: 'none',
                    background: 'transparent',
                    color: colors.textSecondary,
                    fontSize: '0.95rem',
                    fontWeight: 650,
                  }}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        ) : null}

        {editing ? (
          <p style={{ margin: '0 0 1.25rem', fontWeight: 650, fontSize: '1.05rem' }}>
            {(typeChoices.find((type) => type.id === initialType) || {}).label || 'Item'}
          </p>
        ) : (
          <fieldset style={{ border: 'none', margin: '0 0 1.5rem', padding: 0, minWidth: 0, maxWidth: '100%', width: '100%' }}>
            <legend style={{ fontWeight: 650, fontSize: '1.05rem', marginBottom: '0.75rem', padding: 0 }}>What is it?</legend>
            <div className="add-item-chips" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', width: '100%', maxWidth: '100%', minWidth: 0 }}>
              {typeChoices.map((type) => {
                const selected = systemType === type.id;
                return (
                  <button
                    key={type.id}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => setSystemType(type.id)}
                    style={chipStyle(selected)}
                  >
                    {type.label}
                  </button>
                );
              })}
            </div>
          </fieldset>
        )}

        <label style={{ display: 'block', marginBottom: '1rem' }}>
          <span style={{ display: 'block', fontWeight: 650, marginBottom: '0.4rem' }}>Name or brand</span>
          <input
            type="text"
            value={brand}
            placeholder="Optional — Carrier, Pentair…"
            autoComplete="off"
            onChange={(event) => setBrand(event.target.value)}
            style={inputStyle}
          />
        </label>

        <div style={{ marginBottom: '1rem', maxWidth: '100%', minWidth: 0 }}>
          <div className="add-item-when" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '0.4rem', maxWidth: '100%', minWidth: 0 }}>
            <span style={{ fontWeight: 650 }}>Installed</span>
            <div style={{ display: 'flex', flex: '0 1 auto', maxWidth: '100%', border: `1px solid ${colors.border}`, borderRadius: '999px', overflow: 'hidden' }}>
              {['year', 'age'].map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setWhenMode(mode)}
                  style={{
                    minHeight: '36px',
                    minWidth: 0,
                    padding: '0.25rem 0.7rem',
                    border: 'none',
                    background: whenMode === mode ? BRAND : 'transparent',
                    color: whenMode === mode ? '#083838' : colors.textSecondary,
                    fontWeight: 650,
                    fontSize: '16px',
                  }}
                >
                  {mode === 'year' ? 'Year' : 'Age'}
                </button>
              ))}
            </div>
          </div>
          {whenMode === 'year' ? (
            <input
              type="number"
              inputMode="numeric"
              min="1900"
              max={year}
              placeholder="Optional — 2018"
              value={installYear}
              onChange={(event) => setInstallYear(event.target.value)}
              style={inputStyle}
            />
          ) : (
            <input
              type="number"
              inputMode="numeric"
              min="0"
              max="150"
              placeholder="Optional — years old"
              value={age}
              onChange={(event) => setAge(event.target.value)}
              style={inputStyle}
            />
          )}
        </div>

        <div style={{ marginBottom: '1.25rem' }}>
          <span style={{ display: 'block', fontWeight: 650, marginBottom: '0.4rem' }}>Photo</span>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '52px',
              borderRadius: '0.75rem',
              border: `1px dashed ${colors.border}`,
              background: colors.surface,
              color: colors.text,
              fontWeight: 650,
              cursor: 'pointer',
            }}
          >
            <input
              type="file"
              accept="image/*"
              onChange={(event) => pickPhoto(event.target.files && event.target.files[0])}
              style={{ display: 'none' }}
            />
            {photoFile ? 'Change photo' : 'Add a photo'}
          </label>
          {photoError && <p style={{ color: '#b45309', margin: '0.4rem 0 0', fontSize: '0.9rem' }}>{photoError}</p>}
          {photoPreview && (
            <img
              src={photoPreview}
              alt="Selected item"
              style={{ width: '100%', maxHeight: '220px', objectFit: 'cover', borderRadius: '0.75rem', marginTop: '0.75rem' }}
            />
          )}
        </div>

        <details
          style={{
            marginBottom: '1.25rem',
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: '0.75rem',
            padding: '0.35rem 0.9rem 0.15rem',
          }}
        >
          <summary
            style={{
              minHeight: '44px',
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              fontWeight: 650,
              listStyle: 'none',
            }}
          >
            More details (optional)
          </summary>
          <div style={{ display: 'grid', gap: '0.75rem', padding: '0.35rem 0 0.9rem' }}>
            {DETAIL_FIELDS.map((field) => (
              <label key={field.key} style={{ display: 'block' }}>
                <span style={{ display: 'block', marginBottom: '0.3rem', color: colors.textSecondary, fontSize: '0.9rem' }}>{field.label}</span>
                {field.key === 'notes' || field.key === 'specs' ? (
                  <textarea
                    value={details[field.key] || ''}
                    rows={3}
                    onChange={(event) => setDetails((current) => ({ ...current, [field.key]: event.target.value }))}
                    style={{ ...inputStyle, minHeight: '88px', resize: 'vertical' }}
                  />
                ) : (
                  <input
                    type="text"
                    value={details[field.key] || ''}
                    onChange={(event) => setDetails((current) => ({ ...current, [field.key]: event.target.value }))}
                    style={inputStyle}
                  />
                )}
              </label>
            ))}
            {legacyExtras.map((field) => (
              <label key={field.key} style={{ display: 'block' }}>
                <span style={{ display: 'block', marginBottom: '0.3rem', color: colors.textSecondary, fontSize: '0.9rem' }}>{field.label}</span>
                <input
                  type="text"
                  value={extras[field.key] || ''}
                  onChange={(event) => setExtras((current) => ({ ...current, [field.key]: event.target.value }))}
                  style={inputStyle}
                />
              </label>
            ))}
          </div>
        </details>

        </div>
        <div
          className="add-item-footer"
          style={{
            flex: '0 0 auto',
            background: colors.background,
            borderTop: `1px solid ${colors.border}`,
            padding: '0.75rem 1.25rem calc(0.75rem + env(safe-area-inset-bottom, 0px))',
            boxSizing: 'border-box',
          }}
        >
        {onDone ? (
          <button
            type="button"
            onClick={addItem}
            disabled={saving || !systemType}
            style={{
              width: '100%',
              minHeight: '48px',
              marginBottom: '0.5rem',
              borderRadius: '0.85rem',
              border: `1px solid ${colors.border}`,
              background: 'transparent',
              color: systemType && !saving ? colors.text : colors.textSecondary,
              fontSize: '1.05rem',
              fontWeight: 650,
              cursor: systemType && !saving ? 'pointer' : 'not-allowed',
            }}
          >
            {saving ? 'Saving...' : 'Add item'}
          </button>
        ) : null}
        <button
          type={onDone ? 'button' : 'submit'}
          onClick={onDone ? finish : undefined}
          disabled={saving || (!onDone && !systemType)}
          style={{
            width: '100%',
            minHeight: '52px',
            border: 'none',
            borderRadius: '0.85rem',
            background: (onDone ? !saving : primaryReady && !saving) ? BRAND : colors.border,
            color: (onDone ? !saving : primaryReady && !saving) ? '#083838' : colors.textSecondary,
            fontSize: '1.05rem',
            fontWeight: 650,
            cursor: (onDone ? !saving : primaryReady && !saving) ? 'pointer' : 'not-allowed',
          }}
        >
          {saving ? 'Saving...' : (onDone ? 'Done' : (editing ? 'Save item' : 'Add item'))}
        </button>
        {onSkip && (
          <button
            type="button"
            onClick={() => onSkip()}
            disabled={saving}
            style={{
              width: '100%',
              minHeight: '44px',
              marginTop: '0.15rem',
              border: 'none',
              background: 'transparent',
              color: colors.textSecondary,
              fontSize: '0.95rem',
              cursor: 'pointer',
            }}
          >
            Skip for now
          </button>
        )}
        </div>
      </form>
    </div>
  );

  if (fullPage) return dialog;
  return <ModalLayer>{dialog}</ModalLayer>;
};

export default InventoryItemForm;
